import { MapContainer, Marker, Circle, Polygon, Polyline, Tooltip, Popup, CircleMarker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useEffect, useState, useRef, Fragment } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { getVehicleIcon, businessStatus, escapeHtmlAttr } from "@/lib/fleet";
import { canEntries, CAN_FIELDS, CAN_ORDER } from "@/lib/can";
import { getUserSettings, speedStr, fmtShort, driverLabel } from "@/lib/userSettings";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { noSignalSegments } from "@/lib/trips";
import { tripFlagIcon } from "@/lib/mapIcons";
import { AddressLabel } from "@/components/AddressLabel";
import { UserRound, Zap, BatteryMedium, Satellite, SignalHigh, X, Users, Warehouse, Fuel, MapPin, Info, Terminal, History, Crosshair, Store, Clock, Gauge, Navigation, Route, Maximize } from "lucide-react";

const fmtHM = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_COLOR = { moving: "#10B981", idling: "#F59E0B", stopped: "#EF4444" };
const STATUS_LABEL = { moving: "En route", idling: "Ralenti", stopped: "Arrêt" };
const statusOf = (p) => p.status || (p.speed > 3 ? "moving" : (p.ignition ? "idling" : "stopped"));

// P0.2.5 — logique cartographique robuste partagée (fonds + fallback territoire).
import { TILES, TileManager, BaseTileLayer, ZoomInfo, MAP_MAX_ZOOM } from "@/components/MapTiles";


const STATUS_COLORS = {
  moving: "#10B981",
  stopped: "#EF4444",
  idling: "#F59E0B",
  offline: "#71717A",
};

const MARINE = ["boat", "jetski", "sailboat"];

export const POI_CATS = {
  client: { label: "Client", icon: Users },
  depot: { label: "Dépôt", icon: Warehouse },
  station: { label: "Station", icon: Fuel },
  fournisseur: { label: "Fournisseur", icon: Store },
  autre: { label: "Autre", icon: MapPin },
};

function poiIcon(poi) {
  const color = poi.color || "#17A2B8";
  const Comp = (POI_CATS[poi.category] || POI_CATS.autre).icon;
  const svg = renderToStaticMarkup(<Comp color={color} size={12} strokeWidth={2.2} />);
  return L.divIcon({
    className: "",
    html: `<div class="poi-dot" style="--c:${color}">${svg}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function poiCentroid(coords) {
  const n = coords.length || 1;
  return {
    lat: coords.reduce((s, c) => s + c.lat, 0) / n,
    lon: coords.reduce((s, c) => s + c.lon, 0) / n,
  };
}

function PoiPopup({ p, onEdit }) {
  const cat = POI_CATS[p.category] || POI_CATS.autre;
  const color = p.color || "#17A2B8";
  return (
    <Popup>
      <div data-testid={`poi-popup-${p.id}`} style={{ minWidth: 150 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 18, borderRadius: 5, background: color, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
            <cat.icon color="#fff" size={11} />
          </span>
          <b style={{ fontSize: 13 }}>{p.name}</b>
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
          {cat.label}{p.radius > 0 ? ` · rayon ${p.radius} m` : ""}
        </div>
        {p.note && <div style={{ fontSize: 11, color: "#334155", marginTop: 4 }}>{p.note}</div>}
        <button type="button" onClick={() => onEdit && onEdit(p)} data-testid={`poi-edit-${p.id}`}
          style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: color, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          Modifier / supprimer
        </button>
      </div>
    </Popup>
  );
}

function PoiDrawController({ active, onMapClick }) {
  useMapEvents({
    click(e) {
      if (active && onMapClick) onMapClick({ lat: e.latlng.lat, lon: e.latlng.lng });
    },
  });
  return null;
}

function Bubble({ icon: Icon, value, color, title }) {
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${color}1A`, border: `1px solid ${color}55`, color, borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700, margin: "0 4px 4px 0", cursor: title ? "help" : "default" }}>
      <Icon size={12} /> {value}
    </span>
  );
}

function PopupBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 3px", cursor: "pointer", color: "#334155", fontSize: 10, fontWeight: 700, lineHeight: 1.1 }}>
      <Icon size={14} /> {label}
    </button>
  );
}

function VehiclePopup({ v, onAction }) {
  const bs = businessStatus(v);
  const color = bs.color;
  const Comp = getVehicleIcon(v.icon);
  const lp = v.last_position || {};
  const s = v.sensors || {};
  const st = getUserSettings();
  const drvLabel = driverLabel(v);
  const batPct = lp.battery_voltage != null ? Math.max(0, Math.min(100, Math.round(((lp.battery_voltage / 1000 - 3.3) / 0.9) * 100))) : null;
  const seenOf = (k) => {
    const t = (lp.sensor_ts || {})[k] || lp.timestamp;
    return t ? `Dernière donnée : ${fmtShort(t)}` : "Aucune donnée reçue";
  };
  return (
    <Popup>
      <div data-testid={`map-popup-${v.id}`} style={{ minWidth: 190 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 24, height: 24, borderRadius: 6, background: color, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
            <Comp color="#fff" size={15} />
          </span>
          <b style={{ fontSize: 13 }}>{v.name}</b>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 800, color }}>{bs.fresh ? speedStr(lp.speed, MARINE.includes(v.icon)) : "—"}</span>
        </div>
        <div data-testid={`map-popup-status-${v.id}`} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, marginTop: 5, color: "#334155", fontWeight: 600 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
          {bs.text}
          {st.track_show_driver && drvLabel && <>
            <span style={{ margin: "0 2px" }}>·</span>
            <UserRound size={12} /> {drvLabel}
          </>}
        </div>
        {st.track_show_last_gps && lp.timestamp && (
          <div data-testid={`map-popup-time-${v.id}`} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, marginTop: 3, color: "#64748b" }}>
            <Clock size={12} />
            Dernière communication : {fmtShort(lp.timestamp)}
          </div>
        )}
        {lp.lat != null && (
          <div data-testid={`map-popup-loc-${v.id}`} style={{ display: "flex", alignItems: "flex-start", gap: 5, fontSize: 11, marginTop: 3, color: "#475569" }}>
            <MapPin size={12} style={{ marginTop: 1, flexShrink: 0 }} />
            <AddressLabel lat={lp.lat} lon={lp.lon} testid={`map-popup-address-${v.id}`} />
          </div>
        )}
        {st.track_show_coords && lp.lat != null && (
          <div style={{ fontSize: 10, marginTop: 3, color: "#94a3b8" }}>{lp.lat.toFixed(5)}, {lp.lon.toFixed(5)}</div>
        )}
        {st.track_show_diagnostics && (
        <div style={{ marginTop: 6 }}>
          {lp.external_voltage != null && s.ext_voltage?.enabled !== false && <Bubble icon={Zap} value={`${(lp.external_voltage / 1000).toFixed(1)} V`} color="#17A2B8" title={seenOf("external_voltage")} />}
          {batPct != null && s.battery?.enabled !== false && <Bubble icon={BatteryMedium} value={`${batPct}%`} color={batPct > 40 ? "#10B981" : "#F59E0B"} title={seenOf("battery_voltage")} />}
          {lp.satellites != null && s.satellites?.enabled !== false && <Bubble icon={Satellite} value={`${lp.satellites} sat`} color="#10B981" title={seenOf("satellites")} />}
          {lp.gsm_signal != null && s.gsm?.enabled !== false && <Bubble icon={SignalHigh} value={`${lp.gsm_signal}/5`} color="#8B5CF6" title={seenOf("gsm_signal")} />}
          {lp.can && canEntries(lp.can).filter((f) => !v.can_display || v.can_display.length === 0 || v.can_display.includes(f.key)).map((f) => (
            <Bubble key={f.key} icon={f.Icon} value={`${f.value}${f.unit ? " " + f.unit : ""}`} color="#0EA5E9" title={seenOf(f.key)} />
          ))}
        </div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 8, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
          <PopupBtn icon={Info} label="Détails" onClick={() => onAction && onAction("details", v)} testid={`popup-details-${v.id}`} />
          <PopupBtn icon={Route} label="Trajets" onClick={() => onAction && onAction("trips", v)} testid={`popup-trips-${v.id}`} />
          <PopupBtn icon={Terminal} label="Commandes" onClick={() => onAction && onAction("commands", v)} testid={`popup-commands-${v.id}`} />
          <PopupBtn icon={History} label="Rejouer 1h" onClick={() => onAction && onAction("replay", v)} testid={`popup-replay-${v.id}`} />
        </div>
        <button onClick={() => onAction && onAction("follow", v)} data-testid={`popup-follow-${v.id}`}
          style={{ width: "100%", marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#F5821F", color: "#fff", border: "none", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          <Crosshair size={13} /> Suivre ce véhicule
        </button>
      </div>
    </Popup>
  );
}

function vehicleIcon(v, dim = false, hovered = false) {
  const sos = !!v.sos_active;
  const color = sos ? "#DC2626" : (STATUS_COLORS[v.status] || "#71717A");
  const angle = v.last_position?.angle || 0;
  const moving = v.status === "moving";
  const Comp = getVehicleIcon(v.icon);
  const svg = renderToStaticMarkup(<Comp color="#ffffff" size={18} strokeWidth={2.2} />);
  // Statut NON uniquement par la couleur : mouvement = flèche (déjà), ralenti = barres, arrêt = carré, SOS = «!».
  const statTxt = sos ? "SOS" : (moving ? "En mouvement" : v.status === "idling" ? "Au ralenti" : "À l'arrêt");
  const title = `${escapeHtmlAttr(v.name || v.plate || "Véhicule")} — ${statTxt}`;
  const glyphStyle = "position:absolute;right:-2px;bottom:-2px;width:12px;height:12px;border-radius:50%;background:#fff;border:1.5px solid var(--c);display:flex;align-items:center;justify-content:center;line-height:0";
  let glyph = "";
  if (sos) glyph = `<span style="${glyphStyle};color:#DC2626;font-size:9px;font-weight:900">!</span>`;
  else if (v.status === "idling") glyph = `<span style="${glyphStyle}"><span style="width:5px;height:6px;border-left:1.5px solid var(--c);border-right:1.5px solid var(--c)"></span></span>`;
  else if (!moving) glyph = `<span style="${glyphStyle}"><span style="width:5px;height:5px;background:var(--c)"></span></span>`;
  return L.divIcon({
    className: "",
    html: `<div class="veh-badge ${sos ? "veh-sos" : (moving && !dim ? "veh-pulse" : "")}${hovered && !sos ? " veh-hover" : ""}" style="--c:${color}; opacity:${dim ? 0.4 : 1}" role="img" aria-label="${title}" title="${title}">
             ${moving && !sos ? `<div class="badge-arrow" style="transform: rotate(${angle}deg)"></div>` : ""}
             <div class="badge-inner">${svg}</div>
             ${glyph}
           </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function playbackIcon(angle = 0, v = null) {
  const Comp = getVehicleIcon(v?.icon);
  const svg = renderToStaticMarkup(<Comp color="#ffffff" size={18} strokeWidth={2.2} />);
  return L.divIcon({
    className: "",
    html: `<div class="veh-badge veh-pulse" style="--c:#F5821F">
             <div class="badge-arrow" style="transform: rotate(${angle}deg)"></div>
             <div class="badge-inner">${svg}</div>
           </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function sensorLines(v) {
  const s = v.sensors || {};
  const lp = v.last_position || {};
  const out = [];
  if (lp.external_voltage != null && s.ext_voltage?.enabled !== false)
    out.push(`${s.ext_voltage?.label || "Alimentation"}: ${(lp.external_voltage / 1000).toFixed(1)} V`);
  if (lp.battery_voltage != null && s.battery?.enabled !== false) {
    const pct = Math.max(0, Math.min(100, Math.round(((lp.battery_voltage / 1000 - 3.3) / 0.9) * 100)));
    out.push(`${s.battery?.label || "Batterie Interne"}: ${pct}%`);
  }
  if (lp.gsm_signal != null && s.gsm?.enabled !== false)
    out.push(`${s.gsm?.label || "Qualité Signal GSM"}: ${lp.gsm_signal}/5`);
  if (lp.satellites != null && s.satellites?.enabled !== false)
    out.push(`${s.satellites?.label || "Satellites"}: ${lp.satellites}`);
  return out;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.panTo(center, { animate: true, duration: 1.2, easeLinearity: 1 });
  }, [center, map]);
  return null;
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1) {
      map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lon])), { padding: [70, 70] });
    }
  }, [points, map]);
  return null;
}

function FitAll({ points, enabled }) {
  const map = useMap();
  const sig = points.map((p) => p.id).sort().join(",");
  useEffect(() => {
    if (!enabled || points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 14, { animate: true }); return; }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lon])), { padding: [70, 70] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, enabled]);
  return null;
}

// Zoom dynamique : recadre sur le véhicule sélectionné
function FocusSelected({ selected, enabled }) {
  const map = useMap();
  const id = selected?.id;
  const lat = selected?.last_position?.lat;
  const lon = selected?.last_position?.lon;
  useEffect(() => {
    if (!enabled || lat == null || lon == null) return;
    map.setView([lat, lon], Math.max(map.getZoom(), 15), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, enabled]);
  return null;
}

// Bouton « Tout voir » : recadre sur l'ensemble des véhicules à la demande
function FitAllOnSignal({ points, signal }) {
  const map = useMap();
  useEffect(() => {
    if (!signal || points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 14, { animate: true }); return; }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lon])), { padding: [70, 70] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal]);
  return null;
}

function destIcon() {
  const svg = renderToStaticMarkup(<MapPin color="#ffffff" size={16} strokeWidth={2.4} />);
  return L.divIcon({ className: "", html: `<div class="veh-badge" style="--c:#F5821F"><div class="badge-inner">${svg}</div></div>`, iconSize: [34, 34], iconAnchor: [17, 34] });
}

function tripArrowIcon(angle = 0, color = "#17A2B8") {
  return L.divIcon({
    className: "",
    html: `<div class="trip-arrow" style="--c:${color}; transform: rotate(${angle}deg)"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

function tripDotIcon(color = "#EF4444") {
  return L.divIcon({
    className: "",
    html: `<div class="trip-dot" style="--c:${color}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    // Annule toute animation en cours au démontage (évite l'erreur Leaflet _leaflet_pos)
    return () => { clearTimeout(t); try { map.stop(); } catch (e) { /* noop */ } };
  }, [map]);
  return null;
}

// LT1.5 — pan doux vers le véhicule survolé UNIQUEMENT s'il est hors viewport,
// avec debounce frontend 120 ms. Jamais de fitBounds, jamais de changement de zoom.
function HoverPan({ vehicles, hoveredVehicleId, disabled }) {
  const map = useMap();
  useEffect(() => {
    if (disabled || !hoveredVehicleId) return undefined;
    const v = (vehicles || []).find((x) => x.id === hoveredVehicleId);
    const lp = v?.last_position;
    if (!lp || lp.lat == null || lp.lon == null) return undefined;
    const t = setTimeout(() => {
      try {
        const ll = L.latLng(lp.lat, lp.lon);
        if (!map.getBounds().pad(-0.2).contains(ll)) {
          map.panTo(ll, { animate: true, duration: 0.6 });
        }
      } catch (e) { /* noop */ }
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredVehicleId, disabled]);
  return null;
}

// TileManager importé depuis @/components/MapTiles (logique partagée Live Tracking + Historique).

function FollowVehicle({ pos }) {
  const map = useMap();
  const started = useRef(false);
  useEffect(() => {
    if (!pos || pos[0] == null) return;
    if (!started.current) {
      started.current = true;
      map.setView(pos, Math.max(map.getZoom(), 15), { animate: true });
    } else {
      // Recentrage fluide sur le véhicule suivi (pan continu au lieu d'un saut sec toutes les 3 s)
      map.panTo(pos, { animate: true, duration: 2.6, easeLinearity: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos && pos[0], pos && pos[1]]);
  return null;
}

// Marqueur véhicule à déplacement continu : interpole la position entre 2 points GPS
// à la VITESSE RÉELLE (durée = temps écoulé entre les 2 points) pour un rendu « live » fluide,
// sans glissement rapide suivi d'un figement (source des « sauts » perçus).
const _havM = (a, b) => {
  const R = 6371000, tr = (d) => (d * Math.PI) / 180;
  const dLat = tr(b[0] - a[0]), dLon = tr(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(tr(a[0])) * Math.cos(tr(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};
function SmoothMarker({ position, ts, icon, animate = true, children, ...rest }) {
  const markerRef = useRef(null);
  const initial = useRef(position);   // position figée passée à react-leaflet (on pilote via setLatLng)
  const prevTs = useRef(ts);
  const raf = useRef(null);
  useEffect(() => {
    const m = markerRef.current;
    if (!m) return;
    const cur = m.getLatLng();          // on repart TOUJOURS de la position réellement affichée
    const from = [cur.lat, cur.lng];
    const to = position;
    const dtSec = ts && prevTs.current ? (new Date(ts) - new Date(prevTs.current)) / 1000 : 3;
    prevTs.current = ts;
    if (raf.current) cancelAnimationFrame(raf.current);
    const dist = _havM(from, to);
    // Snap immédiat : anim off, position identique, téléportation (>30 km) ou trou de données (>120 s)
    if (!animate || (from[0] === to[0] && from[1] === to[1]) || dist > 30000 || dtSec > 120 || dtSec <= 0) {
      m.setLatLng(to);
      return undefined;
    }
    const dur = Math.min(20000, Math.max(1200, dtSec * 1000)); // durée = temps réel écoulé (borné 1,2–20 s)
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / dur);
      m.setLatLng([from[0] + (to[0] - from[0]) * k, from[1] + (to[1] - from[1]) * k]);
      if (k < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position[0], position[1], ts, animate]);
  return <Marker ref={markerRef} position={initial.current} icon={icon} {...rest}>{children}</Marker>;
}


export default function FleetMap({
  vehicles = [], geofences = [], track = [], selected, onSelect, onQuickAction,
  playback = null, focus = null,
  pois = [], poiFilters = { zones: true, logos: true, labels: true },
  poiDrawMode = null, poiDraft = null, onPoiMapClick, onPoiClick,
  tripPath = [], routePath = [], routeDest = null, fitAll = false, follow = null, followTrace = [], draftWidth = null,
  userPos = null, placingMe = false, onPickMe = null, tripLive = false,
  hoveredVehicleId = null, followId = null, navVehicleId = null, hoverPanDisabled = false,
}) {
  const center = focus
    ? focus
    : selected?.last_position
    ? [selected.last_position.lat, selected.last_position.lon]
    : [-22.2758, 166.458];

  const isPlayback = !!playback;
  const traveled = isPlayback ? playback.track.slice(0, playback.idx + 1) : [];
  const cur = isPlayback ? playback.track[playback.idx] : null;
  const tripStep = tripPath.length > 150 ? Math.ceil(tripPath.length / 150) : 1;
  const tripArrows = tripStep > 1 ? tripPath.filter((_, i) => i % tripStep === 0) : tripPath;
  const [layer, setLayer] = useState(() => getUserSettings().map_type || "satellite");
  const layerManual = useRef(false);
  const { settings: uSettings } = useSettings();
  useEffect(() => {
    if (!layerManual.current && uSettings?.map_type) setLayer(uSettings.map_type);
  }, [uSettings?.map_type]);
  const pickLayer = (k) => { layerManual.current = true; setLayer(k); };
  const [territory, setTerritory] = useState("nc");  // P0.2.5 — territoire courant (nc|world)
  const [fitSig, setFitSig] = useState(0);
  const iconCache = useRef({});
  const getIcon = (v) => {
    // Icône mémoïsée (cap arrondi à 15°) : garde un élément DOM stable entre les rafraîchissements
    // → pas de re-création/clignotement toutes les 3 s (source de « sauts » perçus).
    const sos = v.sos_active ? 1 : 0;
    const bucket = Math.round((v.last_position?.angle || 0) / 15) * 15;
    const sig = `${v.icon}|${v.status}|${sos}|${isPlayback ? 1 : 0}|${bucket}`;
    let ic = iconCache.current[sig];
    if (!ic) { ic = vehicleIcon(v, isPlayback); iconCache.current[sig] = ic; }
    return ic;
  };
  const allPts = (vehicles || [])
    .filter((v) => v?.last_position?.lat != null && v?.last_position?.lon != null)
    .map((v) => ({ id: v.id, lat: v.last_position.lat, lon: v.last_position.lon }));
  const showAll = !!fitAll && allPts.length > 0;
  const uset = uSettings || getUserSettings();

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <div className="absolute top-14 right-3 z-[520] flex flex-wrap justify-end gap-1 glass rounded-md p-1 max-w-[calc(100%-5.5rem)] lg:top-[4.5rem] lg:left-1/2 lg:right-auto lg:-translate-x-1/2 lg:flex-nowrap lg:max-w-none" data-testid="map-layers">
        {Object.entries(TILES).map(([k, t]) => {
          const disabled = t.region === "nc" && territory !== "nc";
          return (
          <button key={k} type="button" onClick={() => !disabled && pickLayer(k)} data-testid={`layer-${k}`} disabled={disabled}
            title={disabled ? "Fond spécifique Nouvelle-Calédonie" : t.label}
            className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${disabled ? "opacity-30 cursor-not-allowed text-muted-foreground" : layer === k ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>{t.label}</button>
          );
        })}
      </div>
      {tripPath.length > 1 && (
        <div className="trip-legend" data-testid="trip-legend">
          <span className="lg"><i style={{ background: "#10B981" }} /> En route</span>
          <span className="lg"><i style={{ background: "#F59E0B" }} /> Ralenti</span>
          <span className="lg"><i style={{ background: "#EF4444" }} /> Arrêt</span>
        </div>
      )}
      {allPts.length > 0 && (
        <button onClick={() => setFitSig((s) => s + 1)} data-testid="map-fit-all" title="Tout voir — recadrer sur tous les véhicules"
          className="absolute bottom-6 right-3 z-[500] glass rounded-md p-2.5 text-muted-foreground hover:text-[#17A2B8] transition-colors">
          <Maximize className="w-4 h-4" />
        </button>
      )}
      <MapContainer center={center} zoom={13} maxZoom={MAP_MAX_ZOOM} zoomControl={false} style={{ height: "100%", width: "100%" }}>
        <InvalidateSize />
        <FitAll points={allPts} enabled={showAll} />
        <FitAllOnSignal points={allPts} signal={fitSig} />
        <FocusSelected selected={selected} enabled={!tripPath.length && !routePath.length && !isPlayback && !follow} />
        <FollowVehicle pos={follow} />
        <HoverPan vehicles={vehicles} hoveredVehicleId={hoveredVehicleId} disabled={hoverPanDisabled || isPlayback} />
        {followTrace.length > 1 && (
          <Polyline positions={followTrace.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#F5821F", weight: 4, opacity: 0.9 }} />
        )}
        <BaseTileLayer layer={layer} />
        <TileManager layer={layer} setLayer={setLayer} onTerritory={setTerritory} />
        <ZoomInfo layer={layer} position="bottomright" />

      {geofences.filter((g) => (g.kind === "interdite" ? uset.show_forbidden_zones : uset.show_geofences)).map((g) =>
        g.type === "circle" && g.center ? (
          <Circle
            key={g.id}
            center={[g.center.lat, g.center.lon]}
            radius={g.radius}
            pathOptions={{ color: g.color, fillColor: g.color, fillOpacity: 0.08, weight: 1.5, dashArray: g.kind === "interdite" ? "6 6" : null }}
          />
        ) : g.type === "polygon" && g.coordinates ? (
          <Polygon
            key={g.id}
            positions={g.coordinates.map((p) => [p.lat, p.lon])}
            pathOptions={{ color: g.color, fillColor: g.color, fillOpacity: 0.08, weight: 1.5, dashArray: g.kind === "interdite" ? "6 6" : null }}
          />
        ) : null
      )}

      {/* POI draw controller + POIs + draft */}
      <PoiDrawController active={!!poiDrawMode} onMapClick={onPoiMapClick} />
      <PoiDrawController active={placingMe} onMapClick={onPickMe} />

      {uset.show_pois && pois.map((p) => {
        const color = p.color || "#17A2B8";
        if (p.type === "troncon" && p.coordinates?.length >= 2) {
          const mid = p.coordinates[Math.floor(p.coordinates.length / 2)];
          return (
            <Fragment key={p.id}>
              {poiFilters.zones && (
                <Polyline positions={p.coordinates.map((pt) => [pt.lat, pt.lon])} pathOptions={{ color, weight: Math.max(6, Math.min(30, (p.width_m || 30) / 2.5)), opacity: 0.22 }} />
              )}
              <Polyline positions={p.coordinates.map((pt) => [pt.lat, pt.lon])} pathOptions={{ color, weight: 3, opacity: 0.95 }} />
              {poiFilters.logos && (
                <Marker position={[mid.lat, mid.lon]} icon={poiIcon(p)}>
                  {poiFilters.labels && !poiDrawMode && <PoiPopup p={p} onEdit={onPoiClick} />}
                </Marker>
              )}
            </Fragment>
          );
        }
        if (p.type === "polygon" && p.coordinates?.length >= 3) {
          const c = poiCentroid(p.coordinates);
          return (
            <Fragment key={p.id}>
              {poiFilters.zones && (
                <Polygon positions={p.coordinates.map((pt) => [pt.lat, pt.lon])}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.06, weight: 2 }} />
              )}
              {poiFilters.logos && (
                <Marker position={[c.lat, c.lon]} icon={poiIcon(p)}>
                  {poiFilters.labels && !poiDrawMode && <PoiPopup p={p} onEdit={onPoiClick} />}
                </Marker>
              )}
            </Fragment>
          );
        }
        if (p.lat == null) return null;
        return (
          <Fragment key={p.id}>
            {poiFilters.zones && p.radius > 0 && (
              <Circle center={[p.lat, p.lon]} radius={p.radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.06, weight: 1.5 }} />
            )}
            {poiFilters.logos && (
              <Marker position={[p.lat, p.lon]} icon={poiIcon(p)}>
                {poiFilters.labels && !poiDrawMode && <PoiPopup p={p} onEdit={onPoiClick} />}
              </Marker>
            )}
          </Fragment>
        );
      })}

      {poiDraft && poiDraft.mode === "point" && poiDraft.lat != null && (
        <Marker position={[poiDraft.lat, poiDraft.lon]} icon={poiIcon(poiDraft)} />
      )}
      {poiDraft && poiDraft.mode === "troncon" && poiDraft.coordinates?.length > 0 && (
        <>
          {draftWidth && <Polyline positions={poiDraft.coordinates.map((c) => [c.lat, c.lon])} pathOptions={{ color: poiDraft.color || "#F5821F", weight: Math.max(6, Math.min(30, draftWidth / 2.5)), opacity: 0.22 }} />}
          <Polyline positions={poiDraft.coordinates.map((c) => [c.lat, c.lon])} pathOptions={{ color: poiDraft.color || "#F5821F", weight: 4, opacity: 0.9 }} />
          {(poiDraft.points || []).map((c, i) => <CircleMarker key={i} center={[c.lat, c.lon]} radius={5} pathOptions={{ color: poiDraft.color || "#F5821F", fillColor: "#fff", fillOpacity: 1, weight: 2 }} />)}
        </>
      )}
      {poiDraft && poiDraft.mode === "polygon" && poiDraft.coordinates?.length > 0 && (
        <>
          <Polyline positions={poiDraft.coordinates.map((c) => [c.lat, c.lon])} pathOptions={{ color: poiDraft.color, weight: 2, dashArray: "4 4" }} />
          {poiDraft.coordinates.map((c, i) => <CircleMarker key={i} center={[c.lat, c.lon]} radius={4} pathOptions={{ color: poiDraft.color }} />)}
        </>
      )}

      {/* live selected track (non-playback) */}
      {!isPlayback && !tripPath.length && track.length > 1 && (
        <Polyline positions={track.map((p) => [p.lat, p.lon])} pathOptions={{ color: selected?.color || "#17A2B8", weight: 3, opacity: 0.8 }} />
      )}

      {/* selected trip: full path with per-point direction/speed */}
      {tripPath.length > 1 && (
        <>
          <FitBounds points={tripPath} />
          <Polyline positions={tripPath.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#17A2B8", weight: 4, opacity: 0.9 }} />
          {noSignalSegments(tripPath).map((s, i) => (
            <Polyline key={`ns-${i}`} positions={s.positions} pathOptions={{ color: "#F59E0B", weight: 5, opacity: 0.95, dashArray: "6 9" }}>
              <Tooltip sticky>Tronçon sans réseau mobile · {s.gapMin} min<br />(données mises en mémoire par le boîtier)</Tooltip>
            </Polyline>
          ))}
          {tripArrows.map((p, i) => {
            const stt = statusOf(p);
            const color = STATUS_COLOR[stt];
            const icon = stt === "moving" ? tripArrowIcon(p.angle, color) : tripDotIcon(color);
            return (
              <Marker key={`tp-${i}`} position={[p.lat, p.lon]} icon={icon}>
                <Popup className="trip-popup">
                  <div data-testid={`trip-point-${i}`} className="tp-card">
                    <div className="tp-head">
                      <span className="tp-chip" style={{ background: color }}>{STATUS_LABEL[stt]}</span>
                      <span className="tp-idx">{i + 1}/{tripArrows.length}</span>
                    </div>
                    <div className="tp-row"><Clock size={13} /><span>{fmtHM(p.timestamp)}</span></div>
                    <div className="tp-row"><Gauge size={13} /><span>{p.speed} km/h</span></div>
                    <div className="tp-row"><Navigation size={13} style={{ transform: `rotate(${p.angle}deg)` }} /><span>{p.angle}° cap</span></div>
                    {p.can && CAN_ORDER.filter((ck) => p.can[ck] != null).length > 0 && (
                      <div style={{ marginTop: 6, paddingTop: 5, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                        <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", opacity: .6, marginBottom: 3 }}>Données CANbus</div>
                        {CAN_ORDER.filter((ck) => p.can[ck] != null).map((ck) => (
                          <div key={ck} className="tp-row" style={{ justifyContent: "space-between" }}>
                            <span style={{ opacity: .7 }}>{(CAN_FIELDS[ck] || {}).label || ck}</span>
                            <b>{p.can[ck]}{(CAN_FIELDS[ck] || {}).unit ? ` ${CAN_FIELDS[ck].unit}` : ""}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <Marker position={[tripPath[0].lat, tripPath[0].lon]} icon={tripFlagIcon("start")}>
            <Popup><b>Départ</b> · {fmtHM(tripPath[0].timestamp)}</Popup>
          </Marker>
          {!tripLive && (
            <Marker position={[tripPath[tripPath.length - 1].lat, tripPath[tripPath.length - 1].lon]} icon={tripFlagIcon("end")}>
              <Popup><b>Arrivée</b> · {fmtHM(tripPath[tripPath.length - 1].timestamp)}</Popup>
            </Marker>
          )}
        </>
      )}

      {/* OSRM route to a destination */}
      {routePath.length > 1 && (
        <>
          <FitBounds points={routePath} />
          <Polyline positions={routePath.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#F5821F", weight: 5, opacity: 0.9 }} />
        </>
      )}
      {routeDest && (
        <Marker position={[routeDest.lat, routeDest.lon]} icon={destIcon()}>
          <Popup><b>Destination</b>{routeDest.label ? <div style={{ fontSize: 11, maxWidth: 200 }}>{routeDest.label}</div> : null}</Popup>
        </Marker>
      )}

      {userPos && (
        <CircleMarker center={[userPos.lat, userPos.lon]} radius={9} pathOptions={{ color: "#fff", fillColor: "#2563EB", fillOpacity: 1, weight: 3 }}>
          <Popup><b>Ma position</b></Popup>
        </CircleMarker>
      )}

      {/* playback: full route faint + traveled bright */}
      {isPlayback && playback.track.length > 1 && (
        <>
          <Polyline positions={playback.track.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#3f3f46", weight: 3, opacity: 0.7 }} />
          {traveled.length > 1 && (
            <Polyline positions={traveled.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#F5821F", weight: 4, opacity: 0.95 }} />
          )}
          {cur && <Marker position={[cur.lat, cur.lon]} icon={playbackIcon(cur.angle, selected)} />}
        </>
      )}

      {/* live vehicle markers (dimmed during playback) */}
      {uset.show_vehicles && vehicles
        .filter((v) => v.last_position)
        .map((v) => {
          const isHover = hoveredVehicleId === v.id;
          const zi = v.sos_active ? 1400 : (selected?.id === v.id ? 1300 : (navVehicleId === v.id ? 1200 : (followId === v.id ? 1100 : (isHover ? 1000 : 0))));
          return (
          <SmoothMarker
            key={`${v.id}-${isHover ? "h" : "n"}`}
            position={[v.last_position.lat, v.last_position.lon]}
            ts={v.last_position.timestamp}
            icon={isHover ? vehicleIcon(v, isPlayback, true) : getIcon(v)}
            animate={!isPlayback}
            zIndexOffset={zi}
          >
            {isHover ? (
              <Tooltip direction="top" offset={[0, -22]} opacity={1} permanent className="veh-hover-label">
                <div data-testid={`hover-label-${v.id}`}>
                  <span style={{ fontWeight: 800, fontSize: 12, display: "block" }}>{v.name}</span>
                  <span data-testid={`hover-driver-${v.id}`} style={{ fontSize: 11, display: "block" }}>
                    {v.current_driver_name || v.driver ? `Conducteur : ${v.current_driver_name || v.driver}` : "Conducteur non identifié"}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.9 }}>{businessStatus(v).fresh ? `${speedStr(v.last_position.speed, MARINE.includes(v.icon))} · ` : ""}{businessStatus(v).text}</span>
                </div>
              </Tooltip>
            ) : (
              <Tooltip direction="top" offset={[0, -18]} opacity={1} permanent={!!uset.vehicle_labels && !!uset.driver_label_on_unit}>
                <span style={{ fontWeight: 700 }}>{v.name}</span>
                <span data-testid={`map-tooltip-driver-${v.id}`} style={{ fontWeight: 400, display: "block" }}>
                  {v.current_driver_name || v.driver ? `Conducteur : ${v.current_driver_name || v.driver}` : "Conducteur non identifié"}
                </span>
              </Tooltip>
            )}
            <VehiclePopup v={v} onAction={onQuickAction} />
          </SmoothMarker>
          );
        })}

      {isPlayback && cur ? (
        <Recenter center={[cur.lat, cur.lon]} />
      ) : selected?.last_position && !tripPath.length && !routePath.length ? (
        <Recenter center={[selected.last_position.lat, selected.last_position.lon]} />
      ) : null}
      </MapContainer>
    </div>
  );
}

